import React, { useState } from 'react';
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from 'date-fns';

export default function TagRemovalDialog({ 
  open, 
  onClose, 
  tagType, 
  tagValue, 
  tagLabel,
  reportIssues,
  progressReports,
  officers,
  onConfirm 
}) {
  const [selectedReports, setSelectedReports] = useState(new Set());
  const [selectAll, setSelectAll] = useState(true);

  // Find all progress reports that have this tag
  const reportsWithTag = React.useMemo(() => {
    const reports = [];
    reportIssues.forEach(issue => {
      const fieldName = tagType;
      const tagArray = issue[fieldName] || [];
      
      // Check if tag is present and not already removed
      const isRemoved = (issue.removed_tags || []).some(
        rt => rt.tag_type === tagType && rt.tag_value === tagValue
      );
      
      if (tagArray.includes(tagValue) && !isRemoved) {
        const report = progressReports.find(r => r.id === issue.progress_report_id);
        if (report) {
          const reporter = officers.find(o => o.user_email === report.reported_by);
          reports.push({
            issueId: issue.id,
            reportId: report.id,
            reportedBy: reporter?.callsign || report.reported_by,
            reportedAt: report.reported_at
          });
        }
      }
    });
    return reports;
  }, [reportIssues, progressReports, officers, tagType, tagValue]);

  // Initialize with all selected
  React.useEffect(() => {
    if (open && reportsWithTag.length > 0) {
      setSelectedReports(new Set(reportsWithTag.map(r => r.issueId)));
      setSelectAll(true);
    }
  }, [open, reportsWithTag]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedReports(new Set());
    } else {
      setSelectedReports(new Set(reportsWithTag.map(r => r.issueId)));
    }
    setSelectAll(!selectAll);
  };

  const handleToggleReport = (issueId) => {
    const newSelected = new Set(selectedReports);
    if (newSelected.has(issueId)) {
      newSelected.delete(issueId);
    } else {
      newSelected.add(issueId);
    }
    setSelectedReports(newSelected);
    setSelectAll(newSelected.size === reportsWithTag.length);
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedReports));
    onClose();
  };

  if (!open) return null;

  return (
    <ConfirmableDialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="act-tagremovaldialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl">
        <DialogHeader>
          <DialogTitle>Remove Tag from Progress Reports</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="p-3 bg-slate-950/50 rounded-lg border border-slate-800">
            <div className="text-sm text-slate-400 mb-1">Removing tag:</div>
            <div className="text-lg font-semibold text-slate-200">{tagLabel}</div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-slate-800">
              <div className="text-sm font-semibold text-slate-300">
                Select Reports ({selectedReports.size} of {reportsWithTag.length})
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSelectAll}
                className="h-8 border-slate-700 hover:bg-slate-800"
              >
                {selectAll ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {reportsWithTag.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No reports found with this tag
                </div>
              ) : (
                reportsWithTag.map((report) => (
                  <div
                    key={report.issueId}
                    className="flex items-center gap-3 p-3 bg-slate-950/50 rounded-lg border border-slate-800 hover:border-slate-700 transition-colors"
                  >
                    <Checkbox
                      checked={selectedReports.has(report.issueId)}
                      onCheckedChange={() => handleToggleReport(report.issueId)}
                      className="border-slate-600"
                    />
                    <div className="flex-1">
                      <div className="text-sm text-slate-300">
                        Reported by <span className="font-semibold">{report.reportedBy}</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {format(new Date(report.reportedAt), 'MMM d, yyyy')} at {format(new Date(report.reportedAt), 'HH:mm')}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="flex gap-2 pt-4 border-t border-slate-800">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 border-slate-700 hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={selectedReports.size === 0}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              Remove from {selectedReports.size} {selectedReports.size === 1 ? 'Report' : 'Reports'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </ConfirmableDialog>
  );
}